import { createRecipeSlug } from "base/utils";
import { RecipeContext } from "components/recipe/providers/RecipeProvider";
import { Link, useParams } from "react-router-dom";
import loadingLogo from "images/BlogFreeRecipeLogo.png";
import logo from "images/BlogFreeRecipe-drkBkgrd.png";
import PageDivider from "base/PageDivider";
import React, { useEffect, useMemo, useRef, useState, useContext } from "react";
import RecipeBadgeDetails from "components/recipe/RecipeBadgeDetails";
import RecipeDirections from "components/recipe/RecipeDirections";
import RecipeIngredientLinkCard from "components/recipe/RecipeIngredientLinkCard";
import RecipeIngredientToolTab from "components/recipe/RecipeIngredientToolTab";
import RecipeKitchenToolLinkCard from "components/recipe/RecipeKitchenToolLinkCard";
import RecipeServingInfo from "components/recipe/RecipeServingInfo";
import RecipeTiming from "components/recipe/RecipeTiming";
import recipeToJsonLd from "searchOptimizations/recipeToJsonLd";

const SITE_URL = "https://www.blogfreerecipe.com";

const RecipeDetails = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [recipe, setRecipe] = useState(null);
  const [servings, setServings] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [retryCount, setRetryCount] = useState(0);

  const headingRef = useRef(null);
  const focusedRouteRef = useRef(null);

  const { id, slug } = useParams();
  const { recipes, featureToggles, fetchRecipeById } = useContext(RecipeContext);

  const routeKey = id || slug || "recipe";

  const amazonLinkStatus = useMemo(
    () => featureToggles?.find((f) => f.name?.includes("Amazon"))?.isEnabled,
    [featureToggles],
  );

  const mainPicture =
    recipe?.main_picture || recipe?.additional_pictures?.[0] || logo;
  /*
   * SEO-friendly slug for this recipe.
   */
  const recipeSlug = useMemo(
    () => (recipe ? createRecipeSlug(recipe) : ""),
    [recipe],
  );
  /*
   * Canonical URL tells search engines which URL
   * should be treated as the primary recipe URL.
   */
  const canonicalUrl = recipeSlug ? `${SITE_URL}/recipes/${recipeSlug}` : "";

  /*
   * Create meta description.
   *
   * recipe-specific description if one exists.
   * Otherwise create one from the recipe name.
   */
  const metaDescription = useMemo(() => {
    if (!recipe) {
      return "Find simple recipes, ingredients, directions, and cooking inspiration at Blog Free Recipe.";
    }

    if (recipe.meta_description) {
      return recipe.meta_description;
    }

    if (recipe.description) {
      return recipe.description
        .replace(/<[^>]*>/g, "")
        .trim()
        .slice(0, 155);
    }

    return `Make ${recipe.recipe_name} with this recipe, including ingredients, cooking times, servings, and step-by-step directions.`;
  }, [recipe]);

  const jsonLd = useMemo(
    () =>
      recipe
        ? recipeToJsonLd({ ...recipe, main_picture: mainPicture }, SITE_URL)
        : null,
    [recipe, mainPicture],
  );

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController()

    async function fetchData() {
      setIsLoading(true);
      setErrorMessage("");
      setRecipe(null);

      try {
        let recipeData = null;

        if (id) {
          recipeData = await fetchRecipeById(id);
        } else if (slug) {
          const contextRecipe = Array.isArray(recipes)
            ? recipes.find((item) => createRecipeSlug(item) === slug)
            : null;

          if (contextRecipe?.id) {
            try {
              recipeData = await fetchRecipeById(contextRecipe.id);
            } catch (error) {
              if (error.name === "AbortError") throw error;
              recipeData = contextRecipe;
            }
          } else {
            recipeData = contextRecipe;
          }
        }

        if (!recipeData) {
          const error = new Error("Recipe not found.");
          error.status = 404;
          throw error;
        }

        if (isMounted) {
          setRecipe(recipeData);
          setServings(recipeData.num_of_servings ?? null);
        }
      } catch (error) {
        if (!isMounted || error.name === "AbortError") return;

        setErrorMessage(
          error.status === 404
            ? "Recipe not found. Try returning to the recipe list."
            : "Unable to load this recipe. Please try again.",
        );
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    fetchData();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [id, slug, recipes, retryCount]);

  useEffect(() => {
    document.title = isLoading
      ? "Loading Recipe | Blog Free Recipe"
      : recipe
      ? `${recipe.recipe_name} Recipe | Blog Free Recipe`
      : "Recipe Unavailable | Blog Free Recipe";
  }, [isLoading, recipe]);

  // Focus the page heading once after each completed route change.
  useEffect(() => {
    if (isLoading || focusedRouteRef.current === routeKey) return;

    if (headingRef.current) {
      headingRef.current.focus({ preventScroll: true });
      focusedRouteRef.current = routeKey;
    }
  }, [isLoading, routeKey, recipe, errorMessage]);

  useEffect(() => {
    if (!recipe) return;

    const pageTitle = `${recipe.recipe_name} Recipe | Blog Free Recipe`;

    const setMetaTag = (selector, attribute, value, content) => {
      let tag = document.head.querySelector(selector);

      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attribute, value);
        document.head.appendChild(tag);
      }

      tag.setAttribute("content", content);
    };

    setMetaTag(
      'meta[name="description"]',
      "name",
      "description",
      metaDescription,
    );

    const socialTags = [
      ["og:title", pageTitle],
      ["og:description", metaDescription],
      ["og:type", "article"],
      ["og:image", mainPicture],
      ...(canonicalUrl ? [["og:url", canonicalUrl]] : []),
    ];

    socialTags.forEach(([name, content]) => {
      setMetaTag(`meta[property="${name}"]`, "property", name, content);
    });

    const twitterTags = [
      ["twitter:card", "summary_large_image"],
      ["twitter:title", pageTitle],
      ["twitter:description", metaDescription],
      ["twitter:image", mainPicture],
    ];

    twitterTags.forEach(([name, content]) => {
      setMetaTag(`meta[name="${name}"]`, "name", name, content);
    });

    if (canonicalUrl) {
      let canonical = document.head.querySelector('link[rel="canonical"]');

      if (!canonical) {
        canonical = document.createElement("link");
        canonical.setAttribute("rel", "canonical");
        document.head.appendChild(canonical);
      }

      canonical.setAttribute("href", canonicalUrl);
    }
  }, [recipe, metaDescription, mainPicture, canonicalUrl]);

  const focusSection = (targetId) => {
    const target = document.getElementById(targetId);

    if (target) {
      target.focus({ preventScroll: true });
      target.scrollIntoView({
        block: "start",
        behavior: "instant",
      });
    }
  };

  const onItemNeeded = (item) => {
    const section = item.split(" ").pop();

    if (section === "Ingredient" || section === "Tool") {
      focusSection(section);
    }
  };

  return (
    <>
      {!isLoading && jsonLd && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(jsonLd).replace(/</g, "\\u003c"),
          }}
        />
      )}

      <div id="top" className="p1 Recipe-Details">
        <p className="Recipe-Details-Status" role="status" aria-atomic="true">
          {isLoading ? "Loading recipe…" : ""}
        </p>

        <div aria-busy={isLoading}>
          {isLoading ? (
            <div className="Justify-Center">
              <img className="loading" src={loadingLogo} alt="" id="Logo" />
            </div>
          ) : recipe ? (
            <>
              <div className="Row marg-1 Space-B">
                <h1 id="recipe-page-title" ref={headingRef} tabIndex={-1}>
                  {recipe.recipe_name}
                </h1>
              </div>

              <div className="Justify-Center">
                <img
                  alt={mainPicture === logo ? "" : recipe.recipe_name}
                  src={mainPicture}
                  className="Large-Logo"
                />
              </div>

              <RecipeTiming recipe={recipe} />
              <PageDivider />

              <div className="p1">
                <RecipeServingInfo
                  recipe={recipe}
                  servings={servings}
                  setServings={setServings}
                />

                <div className="p1">
                  <RecipeBadgeDetails badges={recipe.badges} />
                </div>
              </div>

              <PageDivider />

              <div className="p1">
                <RecipeIngredientToolTab
                  recipe={recipe}
                  itemNeeded={onItemNeeded}
                  servings={servings}
                />
              </div>

              <PageDivider />
              <RecipeDirections recipe={recipe} />

              {amazonLinkStatus && (
                <>
                  <PageDivider />

                  <section
                    id="Ingredient"
                    className="Linked-Sections"
                    tabIndex={-1}
                    aria-labelledby="purchase-ingredients-heading"
                  >
                    <h2
                      id="purchase-ingredients-heading"
                      className="Linked-Titles"
                    >
                      Purchase Ingredients
                    </h2>

                    {recipe.ingredients?.length > 0 && (
                      <RecipeIngredientLinkCard
                        ingredients={recipe.ingredients}
                      />
                    )}

                    <p className="Disclaimer-Text">
                      Please note: the ingredients in this section are
                      suggestions, and may not be the exact product used by the
                      recipe's author.
                    </p>
                  </section>

                  <PageDivider />

                  <section
                    id="Tool"
                    className="Linked-Sections"
                    tabIndex={-1}
                    aria-labelledby="purchase-tools-heading"
                  >
                    <h2 id="purchase-tools-heading" className="Linked-Titles">
                      Purchase Kitchen Tools
                    </h2>

                    {recipe.kitchen_tools?.length > 0 && (
                      <RecipeKitchenToolLinkCard
                        kitchenTool={recipe.kitchen_tools}
                      />
                    )}

                    <p className="Disclaimer-Text">
                      Please note: the kitchen tools in this section are
                      suggestions, and may not be the exact product used by the
                      recipe's author.
                    </p>
                  </section>
                </>
              )}
              
              <div className="Recipe-Pinterest">
                <a
                  href={
                    "https://www.pinterest.com/pin/create/button/?" +
                    new URLSearchParams({
                      url:
                        canonicalUrl ||
                        `${SITE_URL}/recipeDetails/${recipe.id}`,
                      media: new URL(mainPicture, SITE_URL).href,
                      description: recipe.recipe_name,
                    }).toString()
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Save ${recipe.recipe_name} to Pinterest`}
                >
                  <img
                    src="https://assets.pinterest.com/images/pidgets/pinit_fg_en_round_red_32.png"
                    alt=""
                    width={32}
                    height={32}
                  />
                </a>
              </div>

              <a
                href="#recipe-page-title"
                className="Row marg-1 Justify-Center Green"
                onClick={(event) => {
                  event.preventDefault();
                  focusSection("recipe-page-title");
                }}
              >
                <span>Top of Recipe</span>
              </a>
            </>
          ) : (
            <div className="Recipe-Error-State">
              <h1 ref={headingRef} tabIndex={-1}>
                Recipe unavailable
              </h1>

              <p>{errorMessage}</p>

              <button
                type="button"
                className="Recipe-Retry-Button"
                onClick={() => {
                  focusedRouteRef.current = null;
                  setRetryCount((count) => count + 1);
                }}
              >
                Retry loading recipe
              </button>

              <p>
                <Link to="/">Return to all recipes</Link>
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default RecipeDetails;
